import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { DEFAULT_ORGANIZATION_USER_ROLE } from "../../constants/organizationUserRoles.js";

/**
 * Dialog for adding a user to an organization.
 * Displays searchable, paginated list of available users.
 */
export default function AddUserToOrgDialog({
  open,
  onClose,
  searchQuery = "",
  onSearchChange,
  availableUsers = [],
  loadingAvailableUsers = false,
  pagination = { total: 0, limit: 10, offset: 0 },
  onPageChange,
  existingUserIds = [],
  onAddUser,
  onReset,
}) {
  const handleClose = () => {
    onReset?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add User to Organization</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Search Users"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          margin="normal"
          size="small"
          placeholder="Search by name or email..."
          sx={{ mb: 2 }}
        />

        {loadingAvailableUsers ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableUsers.map((user) => {
                  const alreadyAdded = existingUserIds.includes(user.id);
                  return (
                    <TableRow key={user.id}>
                      <TableCell>{user.name || "N/A"}</TableCell>
                      <TableCell>{user.email || "N/A"}</TableCell>
                      <TableCell align="right">
                        {alreadyAdded ? (
                          <Typography variant="body2" color="text.secondary">
                            Already in organization
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => onAddUser(user.id, DEFAULT_ORGANIZATION_USER_ROLE)}
                            sx={{ minWidth: "auto", px: 1.5 }}
                          >
                            Add
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {availableUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary">
                        {searchQuery
                          ? "No users found matching your search"
                          : "No users found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loadingAvailableUsers && pagination.total > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={Math.ceil(pagination.total / pagination.limit)}
              page={Math.floor(pagination.offset / pagination.limit) + 1}
              onChange={(event, page) => {
                const newOffset = (page - 1) * pagination.limit;
                onPageChange(newOffset);
              }}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
